import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";

import { AddTaskModal } from "components/AddTaskModal/AddTaskModal";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { Pill } from "components/ui/Pill";
import { PILL_COLORS } from "components/ui/Pill/pillColors";
import { useClaimTask } from "features/comunications/hooks/useClaimTask";
import { useCompleteTask } from "features/comunications/hooks/useCompleteTask";
import { useTaskById } from "features/comunications/hooks/useTaskById";
import { canClaimTask, getTaskAssignmentLabel } from "features/comunications/utils/formatTaskAssignment";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { useOpenPatientSummaryPage } from "utils/openPatientSummaryPage";

import style from "./DisplayTaskModal.module.css";

const PRIORITY_LABELS = { 0: "High", 1: "Medium", 2: "Low" };
const PRIORITY_PILL = {
  0: PILL_COLORS.reactionYes,
  1: PILL_COLORS.statusWarning,
  2: PILL_COLORS.reactionNo,
};
const STATUS_PILL = {
  Open: PILL_COLORS.reactionUnknown,
  Held: PILL_COLORS.statusWarning,
  Cancelled: PILL_COLORS.reactionYes,
  Complete: PILL_COLORS.reactionNo,
};

export const DisplayTaskModal = ({ headerTitle, closeModal, task, taskId, isOpen }) => {
  const nameState =
    useSelector((state) => state.auth.user?.name) ||
    localStorage.getItem("kinesin-name");

  const { data: fetchedTask, isLoading } = useTaskById(taskId);
  const currentTask = task || fetchedTask;

  const queryClient = useQueryClient();
  const { mutate: completeTask } = useCompleteTask(closeModal);
  const { mutate: claimTask, isPending: isClaiming } = useClaimTask();
  const { data: staffInfo } = useStaffInfo();

  const openPatientSummaryPage = useOpenPatientSummaryPage();

  const handleCloseModal = () => {
    closeModal();
    queryClient.invalidateQueries({ queryKey: ["homeTasks"] });
  };

  if (!isOpen) return null;

  if (taskId && isLoading) {
    return (
      <Dialog open={true} onClose={handleCloseModal} title={headerTitle || "Task Info"} width={640}>
        <div className={style.taskInfo}>
          <p>Loading task...</p>
        </div>
      </Dialog>
    );
  }

  if (!currentTask) return null;

  return (
    <Dialog open={true} onClose={handleCloseModal} title={headerTitle || "Task Info"} width={640}>
      <div className={style.taskInfo}>
        <div>
          <label>Patient name </label>
          <Button
              variant="text"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (currentTask && currentTask.patient)
                  openPatientSummaryPage(
                      currentTask.patient.id,
                      `${currentTask.patient.firstName} ${currentTask.patient.surname}`
                  );
              }}
          >
            {currentTask && currentTask.patient
                ? `${currentTask.patient.firstName} ${currentTask.patient.surname}`
                : "-"}
          </Button>
        </div>
        <div>
          <label>Assigned To</label>
          <p>{getTaskAssignmentLabel(currentTask)}</p>
        </div>
        <div>
          <label>From</label>
          <p>
            {currentTask?.taskCreatedBy
              ? `${currentTask.taskCreatedBy.firstName ?? ""} ${
                  currentTask.taskCreatedBy.lastName ?? ""
                }`.trim() || nameState || "-"
              : nameState || "-"}
          </p>
        </div>
        <div>
          <label>Due date</label>
          <p>
            {currentTask && currentTask.dueDate
                ? moment(currentTask.dueDate).format("DD.MM.YYYY")
                : "-"}
          </p>
        </div>
        <div>
          <label>Priority</label>
          {currentTask && PRIORITY_LABELS[currentTask.priority] ? (
            <Pill {...PRIORITY_PILL[currentTask.priority]}>
              {PRIORITY_LABELS[currentTask.priority]}
            </Pill>
          ) : <p>-</p>}
        </div>
        <div>
          <label>Status</label>
          {currentTask?.status ? (
            <Pill {...(STATUS_PILL[currentTask.status] || PILL_COLORS.reactionUnknown)}>
              {currentTask.status}
            </Pill>
          ) : <p>-</p>}
        </div>
        <div className={style.taskInfoSpanCol2}>
          <label>Instruction</label>
          <p>{currentTask && currentTask.title ? currentTask.title : "-"}</p>
        </div>
        <div className={style.taskInfoSpanCol2}>
          <label>Assignee Comment</label>
          <p>{currentTask && currentTask.assigneeComments ? currentTask.assigneeComments : "-"}</p>
        </div>
      </div>
      <div className={style.buttonContainer}>
        <AddTaskModal
          task={currentTask}
          modalTitle="Edit Task"
          buttonText="Edit Task"
          buttonVariant="outline"
        />
        {currentTask && canClaimTask(currentTask, staffInfo?.staffMember) && (
            <Button
              variant="outline"
              size="large"
              disabled={isClaiming}
              onClick={() => claimTask(currentTask.id)}
            >
              {isClaiming ? "Claiming..." : "Claim Task"}
            </Button>
        )}
        {currentTask && currentTask.status !== "Complete" && (
            <Button
              variant="primary"
              size="large"
              onClick={() => completeTask(currentTask.id)}
            >
              Complete Task
            </Button>
        )}
      </div>
    </Dialog>
  );
};
